import * as React from 'react';
import Modal from 'antd/lib/modal';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Checkbox from 'antd/lib/checkbox';
import Radio from 'antd/lib/radio';
import Select from 'antd/lib/select';
import Alert from 'antd/lib/alert';
import { useSelector } from 'react-redux';
import { selectCanRequestHotfix } from '@/selectors/selectors';
import type { RootState } from '@/reducers/reducers';
import type { HotfixRequestScope } from '@/types/hotfixRequests';
import {
    useCreateHotfixRequestMutation,
    useGetActiveHotfixRequestsQuery,
    useGetHotfixRequestSettingsQuery,
    hotfixRequestErrorMessage,
} from '@/middlewares/defaultApi';
import {
    selectAuthCredentials,
    selectAppStoreType,
    selectIntegrationForCapability,
    selectDestinationInfoByDestinationStepConfigId,
    selectAppSettings,
} from '@/selectors/selectors';
import {
    AutomationType,
    IntegrationCapability,
    IntegrationType,
    RollbackResigningStrategy,
    AllRollbackResigningStrategies,
    SubscriptionFeatureType,
    type ReleaseTag,
    type SelectableCommit,
} from '@/types';
import * as API from '@/api';
import { CreateEditReleaseType } from '@/actions/release';
import { appStoreSupportsRollbacks, getIsGooglePlayStore } from '@/helpers';
import { isValidCommitHashFormat } from '@/helpers/formHelpers';
import { displayStringForDestination, displayTextForResigningStrategy } from '@/helpers/displayHelpers';
import { ReleaseVersionFormItem } from '../prepareRelease/ReleaseVersionFormItem';
import { ReleaseNameFormItem } from '../prepareRelease/ReleaseNameFormItem';
import { MatchPhasedReleaseCheckbox } from '../prepareRelease/MatchPhasedReleaseCheckbox';
import { CommitSelector } from '../CommitSelectorComponent';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { globalMessage } from '@/utils/globalMessage';
import { buildHotfixProposal, validateProposalVersion, type RequestHotfixFormValues } from './proposal';
import MarkdownComponent from '../HOCs/Markdown';
import { antGrey } from '../styles/globalStyles';

export interface RequestHotfixModalProps extends HotfixRequestScope {
    open: boolean;
    onCancel: () => void;
    releaseId?: string;
}
export default function RequestHotfixModal({ open, onCancel, orgId, appId, releaseId }: RequestHotfixModalProps) {
    const [form] = Form.useForm<RequestHotfixFormValues>();
    const releaseType = Form.useWatch('releaseType', form) ?? 'hotfix';
    const baseName = Form.useWatch('baseForHotfix', form);
    const allDestinations = Form.useWatch('allDestinations', form) ?? true;
    const shouldMatchPreviousPhasedRelease = Form.useWatch('shouldMatchPreviousPhasedRelease', form) ?? false;
    const auth = useSelector(selectAuthCredentials);
    const canRequest = useSelector((state: RootState) => selectCanRequestHotfix(state, { orgId, appId }, releaseId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const vcs = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.VersionControl),
    );
    const settings = useSelector((state: RootState) => selectAppSettings(state, appId));
    const destinationsById = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const destinations = React.useMemo(
        () =>
            Object.entries(destinationsById).map(([stepConfigId, destination]) => ({
                ...destination,
                stepConfigId: destination.stepConfigId || stepConfigId,
            })),
        [destinationsById],
    );
    const hotfixPattern = settings?.releaseBranchPatterns?.find((pattern) => pattern.isHotfix);
    const [advisoryStaticBranch, setAdvisoryStaticBranch] = React.useState<boolean | undefined>();
    const isStaticBranch = advisoryStaticBranch ?? !!hotfixPattern?.isStatic;
    const showCommits = vcs?.id === IntegrationType.GitHubVCS || vcs?.id === IntegrationType.GitLabVCS;
    const showPhased = getIsGooglePlayStore(appStoreType);
    const scope = { orgId, appId };
    const hotfixSettings = useGetHotfixRequestSettingsQuery(scope, { skip: !open });
    const requests = useGetActiveHotfixRequestsQuery(scope, { skip: !open || !canRequest });
    const hasActiveRequest = (requests.currentData?.length ?? 0) > 0;
    const [createRequest, mutation] = useCreateHotfixRequestMutation();
    const [bases, setBases] = React.useState<ReleaseTag[]>([]);
    const [sources, setSources] = React.useState<string[]>([]);
    const [commits, setCommits] = React.useState<SelectableCommit[]>([]);
    const [loadingBases, setLoadingBases] = React.useState(false);
    const [loadingCommits, setLoadingCommits] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const searchedCommitHash = isValidCommitHashFormat(search) ? search : undefined;
    const [lookupError, setLookupError] = React.useState('');
    const [submitError, setSubmitError] = React.useState('');
    const submitting = React.useRef(false);
    const disabled =
        !canRequest ||
        hasActiveRequest ||
        requests.isFetching ||
        hotfixSettings.isFetching ||
        !!hotfixSettings.error ||
        !hotfixSettings.currentData ||
        !!requests.error ||
        !requests.currentData ||
        mutation.isLoading;

    React.useEffect(() => {
        if (!open) return;
        form.resetFields();
        setSubmitError('');
        setAdvisoryStaticBranch(undefined);
        setLookupError('');
        setBases([]);
        setSources([]);
        setCommits([]);
        setSearch('');
    }, [open, orgId, appId, form]);

    React.useEffect(() => {
        if (!open || !canRequest) return undefined;
        let cancelled = false;
        setLoadingBases(true);
        API.getPossibleBaseTags({ orgId, appId }, auth)
            .then((tags) => {
                if (cancelled) return;
                if (tags?.length)
                    setBases((previous) => [
                        ...tags,
                        ...previous.filter((base) => !tags.some((tag) => tag.name === base.name)),
                    ]);
                if (tags?.[0] && !form.getFieldValue('baseForHotfix'))
                    form.setFieldValue('baseForHotfix', tags[0].name);
            })
            .catch(() => {
                if (!cancelled)
                    setLookupError('Unable to load base tags. You can still record a proposal without a base.');
            })
            .finally(() => {
                if (!cancelled) setLoadingBases(false);
            });
        if (appStoreSupportsRollbacks(appStoreType)) {
            API.getPossibleSourceVersions({ orgId, appId }, auth)
                .then((versions) => {
                    if (cancelled) return;
                    setSources(versions);
                    if (versions[0] && !form.getFieldValue('sourceVersion'))
                        form.setFieldValue('sourceVersion', versions[0]);
                })
                .catch(() => {
                    if (!cancelled) setLookupError('Unable to load rollback sources. Please try reopening the modal.');
                });
        }
        API.predictReleaseVersion({ orgId, appId, isHotfix: true }, auth)
            .then((version) => {
                if (!cancelled && !form.isFieldTouched('releaseVersion')) form.setFieldValue('releaseVersion', version);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [open, canRequest, orgId, appId, auth, appStoreType, form]);

    React.useEffect(() => {
        if (!open || !canRequest || !showCommits || releaseType !== 'hotfix') return undefined;
        let cancelled = false;
        const commitHash = searchedCommitHash;
        setLoadingCommits(true);
        const baseTag = bases.find((base) => base.name === baseName)?.version;
        API.getCandidateSelectableCommits({ orgId, appId, baseTag, commitHash }, auth)
            .then((result) => {
                if (cancelled) return;
                setCommits((previous) =>
                    commitHash ?
                        [
                            ...previous.filter((commit) => !result.commits.some((found) => found.hash === commit.hash)),
                            ...result.commits,
                        ]
                    :   result.commits,
                );
            })
            .catch(() => {
                if (!cancelled) setLookupError('Unable to load commits.');
            })
            .finally(() => {
                if (!cancelled) setLoadingCommits(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, canRequest, showCommits, releaseType, searchedCommitHash, baseName, bases, orgId, appId, auth]);

    const validateVersion = React.useCallback(
        async (version: string): Promise<void> => {
            await validateProposalVersion(version);
            if (!canRequest || releaseType !== 'hotfix') return;

            const result = await API.validateReleaseVersion(
                { orgId, appId, version, releaseId: undefined, releaseType: CreateEditReleaseType.Hotfix },
                auth,
            ).catch(() => undefined);
            if (!result) return;

            setAdvisoryStaticBranch(result.isStaticBranch);
            if (result.previousTags?.length) {
                const tags = result.previousTags;
                const selected = form.getFieldValue('baseForHotfix');
                setBases((previous) => [
                    ...tags,
                    ...previous.filter((base) => base.name === selected && !tags.some((tag) => tag.name === selected)),
                ]);
                if (!form.isFieldTouched('baseForHotfix')) {
                    form.setFieldValue('baseForHotfix', result.hotfixBase ?? result.previousTags[0].name);
                }
            }
            if (result.validationError) throw new Error(result.validationError);
        },
        [canRequest, releaseType, orgId, appId, auth, form],
    );

    const submit = async (values: RequestHotfixFormValues, canCherryPick: boolean, canRollback: boolean) => {
        if (disabled || submitting.current || (values.releaseType === 'rollback' && !canRollback)) return;
        submitting.current = true;
        setSubmitError('');
        try {
            await createRequest({
                ...scope,
                proposal: buildHotfixProposal(values, bases, isStaticBranch, showCommits && canCherryPick, showPhased),
                justification: values.formFields?.justification?.trim() ?? '',
                formFieldValues: (hotfixSettings.currentData?.hotfixRequestFormFieldsSettings ?? []).map((field) => {
                    const fieldId = field.name.trim().toLowerCase().replace(/\s+/g, '-');
                    return { fieldId, value: values.formFields?.[fieldId] ?? '' };
                }),
            }).unwrap();
            globalMessage.success('Hotfix request created.');
            onCancel();
        } catch (error) {
            setSubmitError(hotfixRequestErrorMessage(error));
        } finally {
            submitting.current = false;
        }
    };
    return (
        <PlanRestrictedFeatureWrapper automationType={AutomationType.AutoCherryPickFromWorkingBranch}>
            {(cherryPickDisabled) => (
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollbacks}>
                    {(rollbackDisabled) => (
                        <Modal
                            open={open}
                            title="Request a hotfix"
                            okText="Request a hotfix"
                            width={650}
                            onCancel={mutation.isLoading ? undefined : onCancel}
                            closable={!mutation.isLoading}
                            maskClosable={!mutation.isLoading}
                            onOk={() => form.submit()}
                            okButtonProps={{
                                disabled:
                                    disabled ||
                                    (releaseType === 'rollback' &&
                                        (rollbackDisabled || !appStoreSupportsRollbacks(appStoreType))),
                                loading: mutation.isLoading,
                            }}
                        >
                            {requests.error && (
                                <Alert
                                    style={{ marginTop: 8 }}
                                    type="error"
                                    message={hotfixRequestErrorMessage(requests.error)}
                                    action={<Button onClick={() => requests.refetch()}>Retry loading</Button>}
                                />
                            )}
                            {hotfixSettings.error && (
                                <Alert
                                    style={{ marginTop: 8 }}
                                    type="error"
                                    message={hotfixRequestErrorMessage(hotfixSettings.error)}
                                    action={<Button onClick={() => hotfixSettings.refetch()}>Retry loading</Button>}
                                />
                            )}
                            {submitError && <Alert style={{ marginTop: 8 }} type="error" message={submitError} />}
                            {lookupError && <Alert style={{ marginTop: 8 }} type="warning" message={lookupError} />}
                            <Form
                                form={form}
                                disabled={disabled}
                                initialValues={{
                                    releaseType: 'hotfix',
                                    shouldCreateBranch: true,
                                    shouldBumpVersionOnBranch: true,
                                    resigningStrategy: RollbackResigningStrategy.LocalResign,
                                    allDestinations: true,
                                    destinationAction: 'include',
                                    shouldMatchPreviousPhasedRelease: false,
                                }}
                                onFinish={(values) =>
                                    submit(
                                        values,
                                        !cherryPickDisabled,
                                        !rollbackDisabled && appStoreSupportsRollbacks(appStoreType),
                                    )
                                }
                            >
                                <ReleaseVersionFormItem disabled={disabled} onValidate={validateVersion} />
                                <ReleaseNameFormItem disabled={disabled} />
                                <Form.Item name="releaseType">
                                    <Radio.Group>
                                        <Radio value="hotfix">Propose a roll-forward hotfix</Radio>
                                        {appStoreSupportsRollbacks(appStoreType) && (
                                            <Radio value="rollback" disabled={disabled || rollbackDisabled}>
                                                Roll back to a previous stable build
                                            </Radio>
                                        )}
                                    </Radio.Group>
                                </Form.Item>
                                {releaseType === 'hotfix' ?
                                    <>
                                        {!isStaticBranch && (
                                            <>
                                                <Form.Item label="Base" name="baseForHotfix">
                                                    <Select
                                                        allowClear
                                                        loading={loadingBases}
                                                        placeholder="Select a base tag"
                                                        options={bases.map((base) => ({
                                                            value: base.name,
                                                            label: base.name,
                                                        }))}
                                                    />
                                                </Form.Item>
                                                <Form.Item name="shouldCreateBranch" valuePropName="checked">
                                                    <Checkbox>Create a new branch</Checkbox>
                                                </Form.Item>
                                            </>
                                        )}
                                        <Form.Item name="shouldBumpVersionOnBranch" valuePropName="checked">
                                            <Checkbox>Bump version</Checkbox>
                                        </Form.Item>
                                        {showCommits && (
                                            <Form.Item label="Commits to cherry-pick" name="commitHashes">
                                                <CommitSelector
                                                    loading={loadingCommits}
                                                    disabled={disabled || cherryPickDisabled}
                                                    commits={commits}
                                                    multiSelect
                                                    allowSearch
                                                    searchValue={search}
                                                    onSearch={setSearch}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>
                                        )}
                                        {showPhased && (
                                            <div style={{ marginBottom: 16 }}>
                                                <MatchPhasedReleaseCheckbox
                                                    visible
                                                    checked={shouldMatchPreviousPhasedRelease}
                                                    disabled={disabled}
                                                    onChange={(checked) =>
                                                        form.setFieldValue('shouldMatchPreviousPhasedRelease', checked)
                                                    }
                                                />
                                            </div>
                                        )}
                                    </>
                                :   <>
                                        <Form.Item
                                            label="Source version"
                                            name="sourceVersion"
                                            rules={[{ required: true }]}
                                        >
                                            <Select
                                                options={sources.map((version) => ({ value: version, label: version }))}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            label="Resigning strategy"
                                            name="resigningStrategy"
                                            rules={[{ required: true }]}
                                        >
                                            <Select
                                                options={AllRollbackResigningStrategies.map((strategy) => ({
                                                    value: strategy,
                                                    label: displayTextForResigningStrategy(strategy),
                                                }))}
                                            />
                                        </Form.Item>
                                    </>
                                }
                                {destinations.length > 1 && (
                                    <>
                                        <Form.Item name="allDestinations" valuePropName="checked">
                                            <Checkbox>Request for all destinations</Checkbox>
                                        </Form.Item>
                                        {!allDestinations && (
                                            <>
                                                <Form.Item name="destinationAction">
                                                    <Select
                                                        options={[
                                                            { value: 'include', label: 'Include' },
                                                            { value: 'exclude', label: 'Exclude' },
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label="Destinations"
                                                    name="destinationIds"
                                                    rules={[{ required: true, type: 'array', min: 1 }]}
                                                >
                                                    <Select
                                                        mode="multiple"
                                                        placeholder="Select destinations"
                                                        optionLabelProp="label"
                                                        maxTagCount="responsive"
                                                    >
                                                        {destinations.map((destination) => {
                                                            const displayName = displayStringForDestination(
                                                                destination.platform,
                                                                destination.appStoreType,
                                                                destination.name,
                                                                destination.appName,
                                                            );
                                                            return (
                                                                <Select.Option
                                                                    key={destination.stepConfigId}
                                                                    value={destination.stepConfigId}
                                                                    label={displayName}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 8,
                                                                        }}
                                                                    >
                                                                        <DestinationCounter
                                                                            destinations={[destination]}
                                                                            size={20}
                                                                        />
                                                                        <span>{displayName}</span>
                                                                    </div>
                                                                </Select.Option>
                                                            );
                                                        })}
                                                    </Select>
                                                </Form.Item>
                                            </>
                                        )}
                                    </>
                                )}
                                {(hotfixSettings.currentData?.hotfixRequestFormFieldsSettings ?? []).map((field) => {
                                    const fieldId = field.name.trim().toLowerCase().replace(/\s+/g, '-');
                                    return (
                                        <React.Fragment key={fieldId}>
                                            {field.description && (
                                                <Form.Item
                                                    label={field.name}
                                                    required={field.required}
                                                    labelCol={{ span: 24 }}
                                                    wrapperCol={{ span: 24 }}
                                                    style={{ marginBottom: 8 }}
                                                >
                                                    <div
                                                        style={{
                                                            marginTop: -8,
                                                            padding: 8,
                                                            border: `1px solid ${antGrey.grey5}`,
                                                            borderRadius: 6,
                                                        }}
                                                    >
                                                        <MarkdownComponent
                                                            source={field.description}
                                                            style={{ fontSize: 12 }}
                                                        />
                                                    </div>
                                                </Form.Item>
                                            )}
                                            <Form.Item
                                                label={field.description ? undefined : field.name}
                                                name={['formFields', fieldId]}
                                                initialValue={field.prePopulatedText}
                                                labelCol={{ span: 24 }}
                                                wrapperCol={{ span: 24 }}
                                                rules={[
                                                    ...(field.required ? [{ required: true, whitespace: true }] : []),
                                                    {
                                                        validator: async (_rule, value?: string) => {
                                                            const characterCount = value?.trim().length ?? 0;
                                                            if (
                                                                characterCount > 0 &&
                                                                characterCount < (field.minCharacters ?? 0)
                                                            ) {
                                                                throw new Error(
                                                                    `${field.name} must contain at least ${field.minCharacters} characters`,
                                                                );
                                                            }
                                                        },
                                                    },
                                                ]}
                                            >
                                                <Input.TextArea rows={4} placeholder={field.placeholder} />
                                            </Form.Item>
                                        </React.Fragment>
                                    );
                                })}
                            </Form>
                        </Modal>
                    )}
                </PlanRestrictedFeatureWrapper>
            )}
        </PlanRestrictedFeatureWrapper>
    );
}
